/**
 * Credit Service
 * Manages user credit operations including balance checks, deductions, and additions.
 *
 * Credit System:
 * - $1 = 10 credits ($0.10 per credit)
 * - AI generation costs 1 credit
 * - New users receive 5 credits ($0.50) upon signup/verification
 */

import { supabase } from '@/config/supabase';

export interface CreditBalance {
  userId: string;
  balance: number;
  updatedAt: string;
}

export interface CreditTransaction {
  id: string;
  userId: string;
  amount: number;
  balanceAfter: number;
  transactionType:
    | 'signup_bonus'
    | 'purchase'
    | 'ai_generation'
    | 'product_payment'
    | 'refund'
    | 'admin_adjustment'
    | 'feedback_reward';
  description?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface DeductCreditsResult {
  success: boolean;
  error?: 'insufficient_credits' | 'user_not_found' | 'unknown';
  message?: string;
  currentBalance?: number;
  required?: number;
  transactionId?: string;
  previousBalance?: number;
  newBalance?: number;
}

export interface AddCreditsResult {
  success: boolean;
  transactionId?: string;
  previousBalance?: number;
  newBalance?: number;
}

/**
 * Get user's current credit balance
 * Returns 0 if user has no credit account yet
 */
const CREDITS_FETCH_TIMEOUT_MS = 5000;

export async function getCredits(userId: string): Promise<number> {
  const fetchPromise = supabase
    .from('user_credits')
    .select('balance')
    .eq('user_id', userId)
    .single();

  let timeoutId: ReturnType<typeof setTimeout>;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error('Credits fetch timed out')),
      CREDITS_FETCH_TIMEOUT_MS
    );
  });

  let result: Awaited<typeof fetchPromise>;
  try {
    result = await Promise.race([fetchPromise, timeoutPromise]);
  } finally {
    clearTimeout(timeoutId!);
  }

  const { data, error } = result;

  if (error) {
    if (error.code === 'PGRST116') {
      // No credits record exists yet, return 0
      return 0;
    }
    console.error('[Credits] Failed to fetch credits:', error);
    throw new Error(`Failed to fetch credits: ${error.message}`);
  }

  return data?.balance ?? 0;
}

/**
 * Check if user has enough credits for an operation
 */
export async function hasEnoughCredits(userId: string, amount: number): Promise<boolean> {
  try {
    const balance = await getCredits(userId);
    return balance >= amount;
  } catch (error) {
    console.error('[Credits] Error checking credit balance:', error);
    return false;
  }
}

/**
 * Deduct credits from user (atomic operation with balance check)
 * Uses Postgres function to prevent race conditions
 *
 * @param userId - The user's ID
 * @param amount - Number of credits to deduct
 * @param transactionType - Type of transaction
 * @param description - Optional description of the transaction
 * @param metadata - Optional metadata to store with transaction
 */
export async function deductCredits(
  userId: string,
  amount: number,
  transactionType: CreditTransaction['transactionType'],
  description?: string,
  metadata?: Record<string, unknown>
): Promise<DeductCreditsResult> {
  try {
    const { data, error } = await supabase.rpc('deduct_credits', {
      p_user_id: userId,
      p_amount: amount,
      p_transaction_type: transactionType,
      p_description: description,
      p_metadata: metadata || {},
    });

    if (error) {
      console.error('[Credits] Credit deduction error:', error);
      return {
        success: false,
        error: 'unknown',
        message: error.message,
      };
    }

    return data as DeductCreditsResult;
  } catch (error) {
    console.error('[Credits] Unexpected error during credit deduction:', error);
    return {
      success: false,
      error: 'unknown',
      message: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Add credits to user account
 * Uses Postgres function to ensure atomicity
 *
 * @param userId - The user's ID
 * @param amount - Number of credits to add
 * @param transactionType - Type of transaction
 * @param description - Optional description of the transaction
 * @param metadata - Optional metadata to store with transaction
 */
export async function addCredits(
  userId: string,
  amount: number,
  transactionType: CreditTransaction['transactionType'],
  description?: string,
  metadata?: Record<string, unknown>
): Promise<AddCreditsResult> {
  try {
    const { data, error } = await supabase.rpc('add_credits', {
      p_user_id: userId,
      p_amount: amount,
      p_transaction_type: transactionType,
      p_description: description,
      p_metadata: metadata || {},
    });

    if (error) {
      console.error('[Credits] Credit addition error:', error);
      throw new Error(`Failed to add credits: ${error.message}`);
    }

    return data as AddCreditsResult;
  } catch (error) {
    console.error('[Credits] Unexpected error during credit addition:', error);
    throw error;
  }
}

/**
 * Get user's credit transaction history
 *
 * @param userId - The user's ID
 * @param limit - Maximum number of transactions to return (default: 50)
 */
export async function getCreditTransactions(
  userId: string,
  limit = 50
): Promise<CreditTransaction[]> {
  const { data, error } = await supabase
    .from('credit_transactions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('[Credits] Failed to fetch transactions:', error);
    throw new Error(`Failed to fetch transactions: ${error.message}`);
  }

  return (data || []).map((tx) => ({
    id: tx.id,
    userId: tx.user_id,
    amount: tx.amount,
    balanceAfter: tx.balance_after,
    transactionType: tx.transaction_type,
    description: tx.description,
    metadata: tx.metadata,
    createdAt: tx.created_at,
  }));
}

/**
 * Calculate credit cost for product total
 * $1 = 10 credits, so $10.00 = 100 credits
 *
 * @param dollars - Dollar amount to convert
 * @returns Number of credits
 */
export function dollarsToCredits(dollars: number): number {
  return Math.round(dollars * 10);
}

/**
 * Calculate dollar value of credits
 *
 * @param credits - Number of credits to convert
 * @returns Dollar amount
 */
export function creditsToDollars(credits: number): number {
  return credits / 10;
}

/**
 * Format credits for display with dollar equivalent
 *
 * @param credits - Number of credits to format
 * @returns Formatted string like "50 credits ($5.00)"
 *
 * @example
 * formatCredits(50) // "50 credits ($5.00)"
 * formatCredits(1) // "1 credit ($0.10)"
 */
export function formatCredits(credits: number): string {
  const dollars = creditsToDollars(credits);
  return `${credits} credit${credits === 1 ? '' : 's'} ($${dollars.toFixed(2)})`;
}
